import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, ArrowLeft, Save, Eye, EyeOff, ZoomIn, X } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FRAMES, getFrame, drawFrame, CANVAS_SIZE, logosReady } from "@/lib/frames";
import { toolkitTitle } from "@/lib/toolkits";
import { usePageMeta } from "@/lib/usePageMeta";

function hasAccess(user, slug) {
  if (!user) return false;
  if (user.is_admin) return true;
  return user.toolkit_scope === "all" || (Array.isArray(user.toolkit_scope) && user.toolkit_scope.includes(slug));
}

export default function AdminToolkitContent() {
  usePageMeta({ title: "Admin · Toolkit Content | Southern Black Girls Toolkits", description: "Staff dashboard." });
  const { user, loading: authLoading } = useAuth();
  const { toolkit } = useParams();

  if (authLoading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
      </div>
    );

  if (!user || !user.is_staff || !hasAccess(user, toolkit))
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <SiteNav />
        <div className="mx-auto max-w-md px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-black text-sbg-burgundy">No access</h1>
          <p className="mt-3 text-sbg-burgundy/70">You don't have access to manage this toolkit's content.</p>
          <div className="mt-6"><Link to="/admin" className="rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">Back to admin</Link></div>
        </div>
        <SiteFooter />
      </div>
    );

  return <Editor toolkit={toolkit} />;
}

function Editor({ toolkit }) {
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [hidden, setHidden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/toolkits/${toolkit}/settings`);
      setHeading(data.about_heading || "");
      setBody(data.about_body || "");
      setHidden(data.hidden_frames || []);
    } catch {
      toast.error("Could not load toolkit content.");
    } finally {
      setLoading(false);
    }
  }, [toolkit]);

  useEffect(() => { load(); }, [load]);

  const toggleFrame = (id) => {
    setHidden((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/toolkits/${toolkit}/settings`, {
        about_heading: heading,
        about_body: body,
        hidden_frames: hidden,
      });
      toast.success("Content saved. Winner pages update instantly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
      </div>
    );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-4xl px-5 py-10">
        <Link to={`/admin/${toolkit}`} data-testid="content-back-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to {toolkitTitle(toolkit)}
        </Link>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sbg-fuchsia text-white">
            <FileText className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-black text-sbg-burgundy" data-testid="content-page-title">Page content</h1>
            <p className="text-sm text-sbg-burgundy/60">Edit the text that shows on every {toolkitTitle(toolkit)} winner page, and hide frames you don't want offered.</p>
          </div>
        </div>

        {/* Text */}
        <div className="mt-8 rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10">
          <h2 className="font-display text-lg font-bold text-sbg-burgundy">Winner page "About" text</h2>
          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-bold text-sbg-burgundy">Heading</span>
            <input
              data-testid="content-heading-input"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-sbg-fuchsia"
            />
          </label>
          <label className="mt-4 block">
            <span className="mb-1.5 block text-sm font-bold text-sbg-burgundy">Body</span>
            <textarea
              data-testid="content-body-input"
              rows={7}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full rounded-xl border border-sbg-raspberry/20 bg-white px-3.5 py-2.5 text-sm leading-relaxed outline-none focus:border-sbg-fuchsia"
            />
          </label>
        </div>

        {/* Frames */}
        <div className="mt-6 rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10">
          <h2 className="font-display text-lg font-bold text-sbg-burgundy">Frames</h2>
          <p className="mt-1 text-sm text-sbg-burgundy/60">Hidden frames won't appear in the frame picker for winners. Click a frame to enlarge it.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...FRAMES].sort((a, b) => (hidden.includes(a.id) ? 1 : 0) - (hidden.includes(b.id) ? 1 : 0)).map((f) => {
              const isHidden = hidden.includes(f.id);
              return (
                <div key={f.id} data-testid={`content-frame-${f.id}`} className={`overflow-hidden rounded-2xl ring-1 ${isHidden ? "bg-sbg-blush/20 ring-sbg-raspberry/10" : "bg-white ring-sbg-raspberry/15"}`}>
                  <div className={`relative ${isHidden ? "opacity-50" : ""}`}>
                    <FramePreview frameId={f.id} onClick={() => setZoom(f.id)} />
                    <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[10px] font-bold text-white"><ZoomIn className="h-3 w-3" /> Enlarge</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 p-3">
                    <div>
                      <div className="text-sm font-bold text-sbg-burgundy">{f.label}</div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-sbg-burgundy/50">{f.group === "sbg" ? "Southern Black Girls" : "Pete & Thomas"}</div>
                    </div>
                    <button
                      data-testid={`content-frame-toggle-${f.id}`}
                      onClick={() => toggleFrame(f.id)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${isHidden ? "bg-sbg-burgundy/80 text-white" : "bg-emerald-100 text-emerald-700"}`}
                    >
                      {isHidden ? <><EyeOff className="h-3.5 w-3.5" /> Hidden</> : <><Eye className="h-3.5 w-3.5" /> Visible</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button data-testid="content-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-7 py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save content
          </button>
        </div>
      </div>

      {zoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setZoom(null)} data-testid="frame-zoom-modal">
          <div className="relative w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoom(null)} data-testid="frame-zoom-close" className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-2 text-sbg-burgundy shadow-lg">
              <X className="h-5 w-5" />
            </button>
            <div className="overflow-hidden rounded-2xl shadow-2xl">
              <FramePreview frameId={zoom} />
            </div>
            <p className="mt-3 text-center font-display text-lg font-bold text-white">{getFrame(zoom).label}</p>
          </div>
        </div>
      )}
      <SiteFooter />
    </div>
  );
}

function FramePreview({ frameId, onClick }) {
  const ref = useRef(null);
  useEffect(() => {
    let active = true;
    logosReady.then(() => {
      const canvas = ref.current;
      if (!active || !canvas) return;
      drawFrame(
        canvas.getContext("2d"),
        null,
        { scale: 1, rotate: 0, offsetX: 0, offsetY: 0, flipH: false },
        getFrame(frameId),
        { name: "", showName: false }
      );
    });
    return () => { active = false; };
  }, [frameId]);
  return (
    <canvas
      ref={ref}
      width={CANVAS_SIZE}
      height={CANVAS_SIZE}
      data-testid={`frame-preview-${frameId}`}
      onClick={onClick}
      style={{ width: "100%", height: "auto", display: "block", cursor: onClick ? "zoom-in" : "default" }}
      className="bg-sbg-blush/30"
    />
  );
}
